import React, { useLayoutEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import Icon from "../../NewInventory/Icon";

/** Above custom PO modals (`Modal.js` uses z-index 1600). */
const TOOLTIP_Z_INDEX = 1700;

const tooltipPopperProps = {
  sx: { zIndex: TOOLTIP_Z_INDEX },
};

const tooltipComponentsProps = {
  tooltip: {
    sx: {
      maxWidth: 360,
      fontSize: 12.5,
      fontWeight: 500,
    },
  },
};

// Cheap, mutation-free truncation check: a single layout read, no DOM
// insert/remove. Reliable for `white-space:nowrap; text-overflow:ellipsis`.
// Avoids the forced reflow that froze slow POS webviews on keyboard resize.
const isTextTruncated = (el) => {
  if (!el) return false;
  if (el.clientWidth <= 0) return false;
  return el.scrollWidth > el.clientWidth + 1;
};

/**
 * Truncates overflowing text with ellipsis. Desktop: hover tooltip when clipped.
 * Touch devices: info icon toggles the full label (no hover on mobile).
 */
const EllipsisTooltip = ({
  title,
  children,
  placement = "top",
  style = {},
  inline = false,
  measureKey,
  alwaysShow = false,
}) => {
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isCoarsePointer = useMediaQuery("(pointer: coarse)");
  const label =
    title ??
    (typeof children === "string" || typeof children === "number"
      ? String(children)
      : "");

  const { flex, display: _display, ...textStyleFromProps } = style;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const check = () => {
      setTruncated(isTextTruncated(el));
    };

    check();
    const raf = window.requestAnimationFrame(check);

    // Observe only the element itself. Since it is width:100%/flex, any parent
    // narrowing shrinks it too and fires this — no ancestor observers, no
    // window.resize listener, so no forced-reflow storm / ResizeObserver
    // feedback loop with react-window on keyboard show/hide.
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    observer?.observe(el);

    return () => {
      window.cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [label, children, measureKey]);

  useLayoutEffect(() => {
    setMobileOpen(false);
  }, [label]);

  const rootStyle = {
    minWidth: 0,
    maxWidth: "100%",
    width: "100%",
    overflow: "hidden",
    display: "block",
    ...(inline ? { flex: flex ?? 1 } : null),
  };

  const textStyle = {
    display: "inline-block",
    maxWidth: "100%",
    verticalAlign: "bottom",
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    boxSizing: "border-box",
    cursor: truncated && isCoarsePointer ? "pointer" : undefined,
    ...textStyleFromProps,
  };

  const textEl = (
    <span ref={ref} style={textStyle}>
      {children ?? label}
    </span>
  );

  const showTooltip = truncated || alwaysShow;

  if (!label) {
    return <div style={rootStyle}>{textEl}</div>;
  }

  if (!showTooltip) {
    return <div style={rootStyle}>{textEl}</div>;
  }

  if (isCoarsePointer && truncated) {
    return (
      <Tooltip
        title={label}
        arrow
        placement={placement}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        PopperProps={tooltipPopperProps}
        componentsProps={tooltipComponentsProps}
      >
        <div
          style={{
            ...rootStyle,
            display: "flex",
            alignItems: "center",
            gap: 4,
            minWidth: 0,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setMobileOpen((open) => !open);
          }}
        >
          {textEl}
          <button
            type="button"
            aria-label="Show full text"
            onClick={(e) => {
              e.stopPropagation();
              setMobileOpen((open) => !open);
            }}
            style={{
              flexShrink: 0,
              width: 22,
              height: 22,
              padding: 0,
              border: "none",
              borderRadius: 6,
              background: "transparent",
              color: "var(--qv-tx-7a818f, #7a818f)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="info" size={14} />
          </button>
        </div>
      </Tooltip>
    );
  }

  return (
    <div style={rootStyle}>
      <Tooltip
        title={label}
        arrow
        placement={placement}
        enterDelay={200}
        PopperProps={tooltipPopperProps}
        componentsProps={tooltipComponentsProps}
      >
        {textEl}
      </Tooltip>
    </div>
  );
};

export default EllipsisTooltip;
